import getConfig from 'next/config';

import ppData from 'shared/seo/data/pp.json';
import { getCountriesForDestination, prepackageCities, prepackageCountries, prepackageMapping } from '../../../data';
import { verticals } from '../../../data/verticals';

import type { Destination } from '../../../interfaces/destination';
import { LinkTree } from 'shared/seo/LinkTree';
import { ExtraLinkTree } from 'shared/seo/ExtraLinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '../../../components/DestinationPage';
import { Offers } from '../../../components/offers/Offers';

const {
    serverRuntimeConfig: { ppOfferUrl },
} = getConfig();

const locationQueryParams = (codes: string[]): string => codes.map((code) => `to=${encodeURI(code)}`).join('&');

export async function getStaticPaths() {
    const theDestinations = prepackageCities.map((dest: Destination) => ({
        params: { city: dest.cityPath, country: dest.countryPath },
    }));
    return {
        paths: theDestinations,
        fallback: false,
    };
}

export async function getStaticProps(context) {
    const destination = prepackageCities.find(
        (entry: Destination) =>
            entry.cityPath === context.params.city.toLowerCase() && entry.countryPath === context.params.country.toLowerCase(),
    );
    const destinationCountry = prepackageCountries.find((entry: Destination) => entry.countryPath === context.params.country.toLowerCase());

    const popularCities = prepackageMapping[destination.id] ?? prepackageMapping[destinationCountry.id] ?? [];
    const popularCountries = getCountriesForDestination(destinationCountry, prepackageCountries);

    const offers = await fetch(`${ppOfferUrl}?${locationQueryParams(destination.locationCodes)}`)
        .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
        .catch((err) => {
            console.warn(err);
            return [];
        });
    return { props: { destination, popularCities, popularCountries, offers } };
}

const City = ({ destination, popularCities, popularCountries, offers }) => (
    <DestinationPage isCountry={false} destination={destination} vertical={verticals.prepackage} data={ppData}>
        <Offers
            destination={destination.city}
            offers={offers}
            vertical={verticals.prepackage}
            heading={ppData.lang.headingOffers}
            showMap={false}
        />
        <PopularCities
            destination={destination}
            cities={popularCities}
            cdnUrl={ppData.cdnPath}
            verticalUrl={ppData.basePath}
            lang={ppData.lang}
        />
        <PopularCountries
            destination={destination}
            countries={popularCountries}
            cdnUrl={ppData.cdnPath}
            verticalUrl={ppData.basePath}
            lang={ppData.lang}
        />
        <LinkTree
            heading={ppData.lang.linkTree.heading}
            description={ppData.lang.linkTree.description}
            data={ppData.linkTree}
            url={ppData.basePath}
        />
        <ExtraLinkTree
            heading={ppData.lang.extraLinkTree.heading}
            linkPrefix={ppData.lang.extraLinkTree.prefix}
            data={ppData.extraLinkTree}
            url={ppData.basePath}
        />
    </DestinationPage>
);

export default City;
